import React, { useRef, useState } from 'react';
import axios from 'axios';

import CardDialog from '~/components/CardDialog';
import { ConfirmModal } from '~/components/ConfirmModal';
import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import TextField from '~/components/core/Molecules/Fields/TextField';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import useDataFetcher from '~/components/useDataFetcher';
import type { IncomingPromotionDetail, PromotionRequestInfo } from '~/server_shared/openapi/data-contracts';
import { prettifyKey, reportAxiosError } from '~/Utils';

import ChangeListByType, { ConflictResolutionSummary } from './ChangeListByType';
import PromotionEnvBadge from './PromotionEnvBadge';
import PromotionExportBlockedBanner from './PromotionExportBlockedBanner';
import PromotionStatusChip from './PromotionStatusChip';
import type { PromotionExportBlocked } from './promotionsUtils';
import {
  assistantDraftedByLabel,
  getPromotionChangeListUnapplicableMessage,
  getPromotionChangeRejectedMessage,
  getPromotionDependencyMissingMessage,
  getPromotionExportBlocked,
  incomingSourceLabel,
  promotionsBaseRoute,
} from './promotionsUtils';
import type { PromotionChange, PromotionConflictResolution } from './types';
import { PROMOTION_STATUS } from './types';
import { resolutionFromConflicts, useConflictResolution } from './useConflictResolution';

interface IncomingPromotionDetailDialogProps {
  requestUuid: string | null;
  organizationId: number;
  onClose: () => void;
  onChanged: () => void;
  /** Fired only when a promotion is actually applied (a clean approve or a resolve-and-apply) — never on
   * reject, and never when approve merely returns conflicts. The apply mutates the target org's config, so
   * the consumer uses this to refresh the target org model (feature/config flags, Claim Bar/Sidebar, etc.). */
  onApplied?: () => void;
}

/** Target-side detail: review the change list and approve/reject a request. A clean approve applies the
 * reviewed diff atomically; if the target has drifted, approve surfaces the per-change conflicts and the
 * approver chooses which to override (apply anyway) vs skip, then re-submits. Gated on
 * SYSCONFIG_CONFIG_MANAGEMENT:FULL. */
const IncomingPromotionDetailDialog: React.FC<IncomingPromotionDetailDialogProps> = ({
  requestUuid,
  organizationId,
  onClose,
  onChanged,
  onApplied,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const baseRoute = promotionsBaseRoute(organizationId);
  const canReview = useHasPermission({
    action: PERMISSION_ACTIONS.SYSCONFIG_CONFIG_MANAGEMENT,
    verb: PERMISSION_VERBS.FULL,
  });
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const [isResolveConfirmOpen, setIsResolveConfirmOpen] = useState(false);
  const [isRejectConfirmOpen, setIsRejectConfirmOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejectReasonDirty, setIsRejectReasonDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // An actionable, operator-fixable error surfaced by an approve/resolve 422 (a missing dependency or a
  // deliberately-rejected change). The request stays pending so approve remains available for retry; the
  // banner carries a per-cause title + the server's message.
  const [actionableError, setActionableError] = useState<{ title: string; note: string } | null>(null);
  // Approve/resolve refused with 422 PROMOTION_EXPORT_BLOCKED: this org's export of a section the promotion
  // writes is blocked, so approve can't take the rollback snapshot it never applies that section without.
  // Listed per offender in-dialog, off Sentry, with the request left approvable once the content is fixed.
  const [exportBlocked, setExportBlocked] = useState<PromotionExportBlocked | null>(null);

  const { isLoading, isError, data } = useDataFetcher<IncomingPromotionDetail>(
    requestUuid ? `${baseRoute}/incoming/${requestUuid}` : null,
    null,
    { shouldFetch: Boolean(requestUuid) }
  );
  const draftedBy = data ? assistantDraftedByLabel(data) : null;

  // Conflicts are not persisted (Design B): they only ever come from a live approve response, so the
  // resolution view is entered from handleApprove/handleResolve below, never hydrated from the detail GET.
  // Reopening a request that previously hit conflicts shows a plain pending request — clicking Approve
  // re-detects against the live target. The per-change resolution mechanics are shared with rollback.
  const cr = useConflictResolution((data?.changes ?? []) as PromotionChange[]);

  const close = () => {
    setIsApproveConfirmOpen(false);
    setIsResolveConfirmOpen(false);
    setIsRejectConfirmOpen(false);
    setRejectReason('');
    setIsRejectReasonDirty(false);
    cr.reset();
    setActionableError(null);
    setExportBlocked(null);
    onClose();
  };

  // A populated `conflicts` body is the "resolution needed" signal — not an HTTP error. Enter (or refresh)
  // the resolution view; an empty body means the apply succeeded.
  const applyApproveResponse = (info: PromotionRequestInfo, { wasResolve }: { wasResolve: boolean }): boolean => {
    const next = resolutionFromConflicts(info);
    if (next) {
      cr.enterResolution(next, { wasResolve });
      // The banner and the decision cards render at the top; the approver may have scrolled down to review.
      dialogRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }
    return true; // applied — caller closes
  };

  // An approve/resolve failure is either an actionable, operator-fixable 422 (missing dependency or a
  // deliberately-rejected change) — shown in-dialog with the request left approvable, off Sentry — or a
  // genuine error routed to the generic modal + Sentry.
  const handleApproveError = async (error: unknown) => {
    const blocked = getPromotionExportBlocked(error);
    if (blocked) {
      setExportBlocked(blocked);
      return;
    }
    const dependencyMessage = getPromotionDependencyMissingMessage(error);
    if (dependencyMessage) {
      setActionableError({ title: 'Missing dependency', note: dependencyMessage });
      return;
    }
    const rejectedMessage = getPromotionChangeRejectedMessage(error);
    if (rejectedMessage) {
      setActionableError({ title: 'Change rejected', note: rejectedMessage });
      return;
    }
    const unapplicableMessage = getPromotionChangeListUnapplicableMessage(error);
    if (unapplicableMessage) {
      setActionableError({ title: 'Re-preview required', note: unapplicableMessage });
      return;
    }
    await reportAxiosError(error);
  };

  const handleApprove = async () => {
    if (!requestUuid) {
      return;
    }
    try {
      setIsSubmitting(true);
      setActionableError(null);
      setExportBlocked(null);
      const { data: info } = await axios.post<PromotionRequestInfo>(`${baseRoute}/approve`, {
        request_uuid: requestUuid,
      });
      if (applyApproveResponse(info, { wasResolve: false })) {
        // Applied: the target org's config was mutated, so refresh its model in addition to the lists.
        onApplied?.();
        onChanged();
        close();
      }
    } catch (error) {
      await handleApproveError(error);
    } finally {
      // Close the confirm modal on every path (success, dependency error, or generic error) so the
      // in-dialog banner is visible and the request stays approvable for retry.
      setIsApproveConfirmOpen(false);
      setIsSubmitting(false);
    }
  };

  const handleResolve = async () => {
    if (!requestUuid || !cr.inResolution) {
      return;
    }
    try {
      setIsSubmitting(true);
      setActionableError(null);
      setExportBlocked(null);
      const { data: info } = await axios.post<PromotionRequestInfo>(`${baseRoute}/approve`, {
        request_uuid: requestUuid,
        override_change_ids: cr.overrideChangeIds,
        expected_conflict_hash: cr.conflictHash,
      });
      if (applyApproveResponse(info, { wasResolve: true })) {
        // Applied: the target org's config was mutated, so refresh its model in addition to the lists.
        onApplied?.();
        onChanged();
        close();
      }
    } catch (error) {
      await handleApproveError(error);
    } finally {
      // Close the confirm modal on every path (success, dependency error, or generic error) so the
      // in-dialog banner is visible and the request stays resolvable for retry.
      setIsResolveConfirmOpen(false);
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!requestUuid || !rejectReason.trim()) {
      return;
    }
    try {
      setIsSubmitting(true);
      await axios.post(`${baseRoute}/reject`, { request_uuid: requestUuid, reason: rejectReason.trim() });
      onChanged();
      close();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPending = data?.status === PROMOTION_STATUS.PENDING;

  const resolutionBanner = (() => {
    if (cr.isStaleRefresh) {
      return {
        title: 'Target changed again',
        note: 'The target changed again before this could be applied — nothing was applied. The changes below have been refreshed; re-check your decisions and try again.',
      };
    }
    if (cr.conflictCount > 0) {
      return {
        title: 'Conflicts detected',
        note: `The target changed after this promotion was created — ${cr.conflictCount} object(s) need a decision because applying the promotion would overwrite a value someone edited on the target. For each, choose “Use incoming” to overwrite it, or “Keep current” to skip that object. Everything else applies automatically.`,
      };
    }
    return {
      title: 'Target changed — nothing to decide',
      note: 'The target changed after this promotion was created, but none of your changes would overwrite those edits. Review the changes below and apply — everything applies automatically.',
    };
  })();

  return (
    <CardDialog
      contentRef={dialogRef}
      isDialog
      title="Review promotion"
      open={Boolean(requestUuid)}
      onClose={close}
      preventClose={isSubmitting}
      fullWidth
      maxWidth="md"
      footerActions={
        <>
          <Button variant="text" onClick={close}>
            Close
          </Button>
          {canReview && cr.inResolution && (
            <>
              <Button
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => setIsRejectConfirmOpen(true)}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting || !cr.allConflictsResolved}
                onClick={() => setIsResolveConfirmOpen(true)}
              >
                Resolve &amp; apply
              </Button>
            </>
          )}
          {canReview && !cr.inResolution && isPending && (
            <>
              <Button
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
                onClick={() => setIsRejectConfirmOpen(true)}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                onClick={() => setIsApproveConfirmOpen(true)}
              >
                Approve
              </Button>
            </>
          )}
        </>
      }
    >
      <LoadingSwitch isLoading={isLoading} isError={isError}>
        {data && (
          <div className="flex flex-col gap-16 py-8">
            <div className="text-lg font-bold">{data.request_name || '—'}</div>
            <div className="flex items-center gap-x-12">
              <span>
                From <span className="font-bold">{incomingSourceLabel(data)}</span>
              </span>
              <PromotionEnvBadge subDomain={data.source_deployment_id} />
              <PromotionStatusChip status={data.status} />
              {draftedBy ? (
                <span className="text-slate-700">{draftedBy}</span>
              ) : (
                data.requester_display_name && (
                  <span className="text-slate-700">
                    Requested by {data.requester_display_name}
                    {data.requester_email && ` (${data.requester_email})`}
                  </span>
                )
              )}
            </div>

            {data.reject_reason && (
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.INFO}
                title={data.status === PROMOTION_STATUS.CANCELLED ? 'Cancellation reason' : 'Rejection reason'}
                note={data.reject_reason}
              />
            )}

            {!cr.inResolution && data.conflict_resolution && (
              <ConflictResolutionSummary resolution={data.conflict_resolution as PromotionConflictResolution} />
            )}

            {actionableError && (
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.ERROR}
                title={actionableError.title}
                note={actionableError.note}
              />
            )}

            {exportBlocked && <PromotionExportBlockedBanner exportBlocked={exportBlocked} />}

            {(data.deferred_configuration_types ?? []).length > 0 && (
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.INFO}
                title="Sections not restored"
                note={`Not restored by this rollback: ${(data.deferred_configuration_types ?? [])
                  .map(prettifyKey)
                  .join(
                    ', '
                  )}. Once the items blocking them are fixed, roll back the original promotion again to restore them.`}
              />
            )}

            {cr.inResolution && (
              <AlertBanner
                withIcon
                alertType={AlertBanner.ALERT_TYPES.WARNING}
                title={resolutionBanner.title}
                note={resolutionBanner.note}
              />
            )}

            <ChangeListByType
              changes={data.changes as PromotionChange[]}
              summary={data.summary}
              conflicts={cr.inResolution ? cr.conflictMap : undefined}
              conflictDecisions={cr.inResolution ? cr.decisions : undefined}
              onResolveConflict={cr.inResolution ? cr.handleResolveConflict : undefined}
            />
          </div>
        )}
      </LoadingSwitch>

      <ConfirmModal
        isOpen={isApproveConfirmOpen}
        isSubmitting={isSubmitting}
        onClose={() => setIsApproveConfirmOpen(false)}
        title="Approve promotion"
        contentText="Apply the reviewed configuration changes to this organization?"
        primaryButtonName="Approve & apply"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleApprove}
      />

      <ConfirmModal
        isOpen={isResolveConfirmOpen}
        isSubmitting={isSubmitting}
        onClose={() => setIsResolveConfirmOpen(false)}
        title="Resolve &amp; apply"
        contentText={`Apply the non-conflicting changes plus the ${
          cr.materialConflictIds.filter((id) => cr.decisions[id] === 'override').length
        } conflict(s) you chose to override (overwriting the target's current value), and skip the rest?`}
        primaryButtonName="Resolve & apply"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleResolve}
      />

      <ConfirmModal
        isOpen={isRejectConfirmOpen}
        isSubmitting={isSubmitting}
        onClose={() => {
          setIsRejectConfirmOpen(false);
          setRejectReason('');
          setIsRejectReasonDirty(false);
        }}
        title="Reject promotion"
        contentText={
          <div className="flex flex-col gap-12">
            <span>Reject this promotion request? The reason is shown to the requester.</span>
            <TextField
              label="Rejection reason"
              size="medium"
              value={rejectReason}
              onChange={(newValue: string) => {
                setRejectReason(newValue);
                setIsRejectReasonDirty(true);
              }}
              error={isRejectReasonDirty && !rejectReason.trim()}
              helperText={isRejectReasonDirty && !rejectReason.trim() ? 'A reason is required' : ''}
              multiline
              rows={3}
              fullWidth
              autoFocus
            />
          </div>
        }
        primaryButtonName="Confirm reject"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleReject}
      />
    </CardDialog>
  );
};

export default IncomingPromotionDetailDialog;
